import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { map, Observable, of } from 'rxjs';

import {
  ClientInfo,
  DianInfo,
  EmitterInfo,
  Totals,
  InvoicePdf,
  NotePdf,
} from '@shared-models';

import {
  InvoiceStatus,
  InvoiceStatusDescription,
} from 'src/app/@enums/pdf/invoice-status.enum';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { ICustomer } from 'src/app/@interfaces/customer.interface';
import { IPrefix } from 'src/app/@interfaces/prefix.interface';
import { IQuota } from 'src/app/@interfaces/quotation.interface';
import { IValoresSingle } from 'src/app/@interfaces/valores.interface';
import { IVentaQuery } from 'src/app/@interfaces/venta.interface';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AppState } from 'src/app/state/app.state';
import { selectCompaniesList } from 'src/app/state/selectors/user-companies.selectors';

@Injectable({
  providedIn: 'root',
})
export class PdfElectronicInfoService {
  private translate = inject(TranslateService);
  private cookieAuthService = inject(CookieAuthService);
  private store = inject<Store<AppState>>(Store);

  buildEmitter(activeCompanyDetails: IStorageCompany) {
    return {
      name: activeCompanyDetails?.name || '',
      nit: activeCompanyDetails?.NIT || '',
      address: activeCompanyDetails?.address || '',
      tributary_regime:
        activeCompanyDetails?.contributor_type.description || '',
      responsibility_code:
        activeCompanyDetails?.contributor_type.dian_code || '',
    };
  }

  getPrefix(activeCompanyDetails: IStorageCompany, voucher: number) {
    return activeCompanyDetails?.prefixes.find((p) => p.voucher === voucher);
  }

  buildDianData(cufeOrCude: string, prefix: IPrefix | undefined) {
    return {
      cufe: cufeOrCude || '',
      resolution: prefix?.invoice_authorization || '',
      range: prefix
        ? `${prefix.prefix}${prefix.prefix_from}-${prefix.prefix}${prefix.prefix_to}`
        : '',
    };
  }

  buildClient(client: ICustomer | undefined) {
    return {
      name: client?.nombre || '',
      id: client?.doc_nit || '',
      address: client?.direccion || '',
      email: client?.email || '',
    };
  }

  buildEmission(
    dueDate: string,
    electronicInvoice: string,
    parsedDate: { date: string; time: string },
  ) {
    return {
      code: electronicInvoice,
      emission_date: `${parsedDate.date} / ${parsedDate.time}`,
      due_date: dueDate || '',
      payment_form: this.translate.instant('Credito'),
    };
  }

  buildTotals(object: InvoicePdf | NotePdf) {
    // object.subtotal is already net of line discounts (see calculateTotals in
    // CalculateDetailsService / CalculateNotePdfService): do not subtract the
    // discount again or the printed subtotal and QR ValFac fall below the taxable base.
    const netSubtotal = object.subtotal;
    const discount = object.discount || 0;
    return {
      grossSubtotal: Number((netSubtotal + discount).toFixed(2)),
      discount: Number(discount.toFixed(2)),
      subtotal: Number(netSubtotal.toFixed(2)),
      iva: Number(object.taxCalculated.toFixed(2)),
      netValue: Number((netSubtotal + object.taxCalculated).toFixed(2)),
      total: Number(object.total.toFixed(2)),
    };
  }

  buildStyles() {
    return {
      title: { fontSize: 10, bold: false, alignment: 'right' },
      titleBold: { fontSize: 32, bold: true, alignment: 'right' },
      invoiceCode: { fontSize: 18, bold: true, alignment: 'right' },
      emmiterBold: { fontSize: 10, bold: true },
      emmiter: { fontSize: 10 },
      cufe: { fontSize: 9, alignment: 'right' },
      sectionHeader: { fontSize: 10, bold: true, margin: [0, 0, 0, 2] },
      client: { fontSize: 10 },
      emission: { fontSize: 10, bold: false },
      tableHeader: {
        fontSize: 10,
        bold: true,
        fillColor: '#e0e0e0',
        border: [false, false, false, false],
        noWrap: true,
        margin: [0, 10, 0, 10],
        alignment: 'left',
      },
      tableCell: { fontSize: 10, alignment: 'center' },
      observations: { fontSize: 10 },
      footer: { fontSize: 8, alignment: 'center', color: '#333' },
    };
  }

  generateQr(
    object: InvoicePdf | NotePdf,
    electronicInvoice: string,
    parsedDate: { date: string; time: string },
    emitter: EmitterInfo,
    client: ClientInfo,
    totals: Totals,
    dian: DianInfo,
  ) {
    // ValFac is the pre-tax subtotal DIAN hashes into the CUFE, i.e. the
    // LineExtensionAmount, which is net of the line discounts — not the total (that
    // one is ValTolFac). DIAN rule FAU06: ValFac + ValIva === ValTolFac.
    return `
      NumFac: ${electronicInvoice}
      FecFac: ${parsedDate.date}
      HorFac: ${parsedDate.time}
      NitFac: ${emitter.nit}
      DocAdq: ${client.id}
      ValFac: ${totals.subtotal.toFixed(2)}
      ValIva: ${totals.iva.toFixed(2)}
      ValOtroIm: ${object.otherTaxCalculated.toFixed(2)}
      ValTolFac: ${totals.total.toFixed(2)}
      CUFE: ${dian.cufe}
      ${object.qrUrl}
    `;
  }

  getActiveCompanyDetail(): Observable<IStorageCompany | null> {
    const activeCompanyFromCookies = this.cookieAuthService.getCompanyObject;

    if (!activeCompanyFromCookies?.Id_company) {
      return of(null);
    }

    return this.store.select(selectCompaniesList).pipe(
      map((companies) => {
        if (!companies.length) return null;
        return (
          companies.find(
            (company) =>
              company.Id_company === activeCompanyFromCookies.Id_company,
          ) || null
        );
      }),
    );
  }

  generatePdfItems(
    sales: IVentaQuery[] | IValoresSingle[],
    invoiceObject: IQuota,
  ) {
    return sales.map((sale, idx) => {
      const quantity = sale.cantidad;
      const taxPercentage = sale.articulo[0]?.tax?.[0]?.value || 0;

      let unitPrice = sale.precio;
      if (invoiceObject.tax_incl === 'true') {
        unitPrice = sale.precio / (1 + taxPercentage);
      }

      const ivaValue = unitPrice * taxPercentage;
      const subtotal = (ivaValue + unitPrice) * quantity;

      return {
        item: idx + 1,
        code: sale.articulo[0]?.codigo || '',
        description: sale.articulo[0]?.descripcion || '',
        quantity: Math.abs(quantity),
        unitPrice: '$' + `${Math.abs(unitPrice).toFixed(2)}`,
        iva: Math.abs(taxPercentage),
        ivaValue: '$' + `${Math.abs(ivaValue).toFixed(2)}`,
        subtotal: '$' + `${Math.abs(subtotal).toFixed(2)}`,
      };
    });
  }

  getPdfInvoiceDate(invoiceObject: IQuota) {
    let date = '';
    switch (invoiceObject.estado) {
      case InvoiceStatus.Quotation:
        date = invoiceObject.createdAt;
        break;
      case InvoiceStatus.Rejected:
        date = invoiceObject.createdAt;
        break;
      case InvoiceStatus.Delivery:
        date = invoiceObject.delivery_date!;
        break;
      case InvoiceStatus.Invoice:
        date = invoiceObject.invoiced_date!;
        break;
      default:
        date = InvoiceStatusDescription.Unknown;
        break;
    }
    return date;
  }
}
